/**
 * `GET /plan-assignment`, read twice — once as a LIST and once as a MAP.
 *
 * The endpoint returns the trainer's whole assignment list, flat, with no parameters. This screen
 * asks two different questions of it:
 *
 * · **`Plany klientów`** — every live assignment as a row, sorted by the client.
 * · **The inspector's strip** — who follows a given TEMPLATE, grouped by `sourceTemplateId`.
 *
 * ── One key, one request, two selects, and that is the whole point ───────────────────────────
 * `planAssignmentListQueryKey` from `entities/plan-draft` — the key the builder's own
 * `planForkCustomerQueryOptions` already uses. TanStack keys the cache ENTRY and each `useQuery`
 * projects from it, so the list, the strip and the builder share one response without any of them
 * knowing about the others. A key of this slice's own would put a second copy of the trainer's
 * whole assignment list in the cache, and the two would drift apart the moment one was invalidated.
 *
 * That is also why both options objects are here rather than one beside the list and one beside the
 * inspector: what they must share is the key, the request and the window, and the only honest way
 * to keep three of a thing in step is to write them once.
 *
 * ── The window is the inspector's five minutes, not the builder's `Infinity` ─────────────────
 * Owner decision, 3 September 2026, and the reasoning is in `inspectorQueries`: the builder OWNS
 * the draft it loaded and must not have it refetched underneath an open editor; this screen owns
 * nothing and is read-only.
 */

import { queryOptions } from '@tanstack/react-query';

import { planAssignmentListQueryKey } from '~/entities/plan-draft';
import { getPlanAssignment } from '~/shared/api/generated/endpoints/plan-assignment/plan-assignment';

import { toClientPlanRows, type ClientPlanRow } from '../model/clientPlanRow';
import type { TranslateKey } from '../model/planLibraryRow';
import { templateFollowers, type PersonView } from '../model/templateFollowers';

/** The owner's window for a read-only preview, 3 September 2026. */
const ASSIGNMENT_STALE_TIME = 5 * 60 * 1000;

/** The read itself. Written once, because three options objects sharing a key must share it exactly. */
function assignmentRead() {
  return {
    queryKey: planAssignmentListQueryKey,
    queryFn: ({ signal }: { signal: AbortSignal }) => getPlanAssignment(undefined, signal),
    staleTime: ASSIGNMENT_STALE_TIME,
    refetchOnWindowFocus: false,
  };
}

/** The `Plany klientów` list: every live assignment as a row, in the client's alphabetical order. */
export function clientPlansQueryOptions(locale: string, t: TranslateKey) {
  return queryOptions({
    ...assignmentRead(),
    select: (assignments): readonly ClientPlanRow[] => toClientPlanRows(assignments, t, locale),
  });
}

/**
 * Every live assignment, grouped by the template it was forked from.
 *
 * The whole map rather than one template's followers: a per-template `select` would read the same
 * cache entry but regroup the trainer's entire assignment list once per pointed row, and the map is
 * built once for the whole screen.
 */
export function templateFollowersQueryOptions(locale: string) {
  return queryOptions({
    ...assignmentRead(),
    select: (assignments): ReadonlyMap<number, readonly PersonView[]> =>
      templateFollowers(assignments, locale),
  });
}
